<template>
  <Teleport to="body">
    <div class="app-notifications-container" :class="containerClasses">
      <TransitionGroup 
        name="notification"
        tag="div"
        class="app-notifications-list"
      >
        <AppNotification
          v-for="notification in limitedNotifications"
          :key="notification.id"
          :notification="notification"
          @close="(notificationId?: number) => removeNotification(notificationId || notification.id)"
          @action="handleAction"
        />
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { computed } from 'vue';
import { useNotifications } from '@/composables/useNotifications';
import AppNotification from './AppNotification.vue';
import type { ToastNotification, ToastAction } from '@/types';

interface Props {
  position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxNotifications: number;
}

interface Emits {
  (e: 'action', action: ToastAction): void;
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top-right',
  maxNotifications: 5
});

const emit = defineEmits<Emits>();

const { notifications, removeNotification } = useNotifications();

// Classes CSS pour la position du conteneur
const containerClasses = computed(() => [
  `app-notifications-container--${props.position}`
]);

// Limiter le nombre de notifications affichées
const limitedNotifications = computed(() => {
  return notifications.value.slice(0, props.maxNotifications);
});

const handleAction = (action: ToastAction) => {
  emit('action', action);
};
</script>

<style scoped>
.app-notifications-container {
  position: fixed;
  z-index: var(--z-notification, 9999);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-width: 480px;
  width: auto;
  pointer-events: auto; /* Permettre les interactions */
}

.app-notifications-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.app-notifications-container > * {
  pointer-events: auto;
}

/* Positions */
.app-notifications-container--top-right {
  top: var(--space-6);
  right: var(--space-6);
}

.app-notifications-container--top-left {
  top: var(--space-6);
  left: var(--space-6);
}

.app-notifications-container--bottom-right {
  bottom: var(--space-6);
  right: var(--space-6);
  flex-direction: column-reverse;
}

.app-notifications-container--bottom-left {
  bottom: var(--space-6);
  left: var(--space-6);
  flex-direction: column-reverse;
}

.app-notifications-container--top-center {
  top: var(--space-6);
  left: 50%;
  transform: translateX(-50%);
}

.app-notifications-container--bottom-center {
  bottom: var(--space-6);
  left: 50%;
  transform: translateX(-50%);
  flex-direction: column-reverse;
}

/* Animations de transition */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.notification-move {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Animations pour positions gauche */
.app-notifications-container--top-left .notification-enter-from,
.app-notifications-container--top-left .notification-leave-to,
.app-notifications-container--bottom-left .notification-enter-from,
.app-notifications-container--bottom-left .notification-leave-to {
  transform: translateX(-100%) scale(0.95);
}

/* Animations pour positions centrales */
.app-notifications-container--top-center .notification-enter-from,
.app-notifications-container--top-center .notification-leave-to,
.app-notifications-container--bottom-center .notification-enter-from,
.app-notifications-container--bottom-center .notification-leave-to {
  transform: translateY(-20px) scale(0.95);
}

/* Responsive */
@media (max-width: 640px) {
  .app-notifications-container {
    top: var(--space-4);
    right: var(--space-4);
    left: var(--space-4);
    max-width: none;
    width: auto;
  }
  
  .app-notifications-container--top-left,
  .app-notifications-container--top-right,
  .app-notifications-container--top-center {
    top: var(--space-4);
    left: var(--space-4);
    right: var(--space-4);
    transform: none;
  }
  
  .app-notifications-container--bottom-left,
  .app-notifications-container--bottom-right,
  .app-notifications-container--bottom-center {
    bottom: var(--space-4);
    left: var(--space-4);
    right: var(--space-4);
    transform: none;
  }
}
</style>
